import React, { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { omit } from 'lodash';
import getRoute from '@root/src/services/routeList';
import { INSURANCE_ACCEPTANCE, PRE_SERVICE_LICENCE_TYPE, REGISTRATION_STATUS } from '@root/src/types';
import config from '@root/src/config';
import { breakpoint, mediaBreakpoint } from '@services/responsiveProvider';
import { selectEventCategory, selectEventDetail } from '@features/eventDetail/slices/eventDetailServerSlice';
import { selectEventCategoryPreServicesGroups } from '@features/eventDetail/slices/eventDetailClientSlice';
import Button from '@features/ui/components/Button';
import Icon, { ICON_NAME } from '@features/ui/components/Icon';
import { textP } from '@features/ui/styles/textStyles';
import { selectCurrentUserDetail, selectIsAuthenticated } from '@features/users/slices/userSlice';
import {
  selectSubscriptionFor,
  selectSubscriptionPack,
  setSubscription,
  setSubscriptionPreServices,
} from '@features/subscriptions/slices/formSubscriptionSlice';
import { VIP_VALIDATION_INPUT } from '@features/ui-input/components/InputVIP';
import Banner from '@features/ui/components/Banner';
import { selectCurrentCartDetail } from '@features/carts/slices/cartsSlice';
import FormPreServices from '@features/eventDetail/components/EventCategoryListContent/FormPreServices';
import { FormikContext } from '@features/serieDetail/components/FormikContextProvider';

interface EventCategoryListContentProps {
  eventId: number;
  categoryId: number;
}

const EventCategoryListContent = ({ eventId, categoryId }: EventCategoryListContentProps) => {
  const router = useRouter();
  const cart = useSelector(selectCurrentCartDetail);
  const category = useSelector(selectEventCategory(eventId, categoryId));
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { formikContexts, addFormikContext } = useContext(FormikContext);

  const logged = useSelector(selectIsAuthenticated);
  const userDetail = useSelector(selectCurrentUserDetail);
  const subscriptionFor = useSelector(selectSubscriptionFor);
  const pack = useSelector(selectSubscriptionPack);
  const preServices = useSelector(selectEventCategoryPreServicesGroups(categoryId));
  const event = useSelector(selectEventDetail(eventId));
  const isRegistrationOpen = event.status.registration === REGISTRATION_STATUS.OPEN
    && !event.quotas?.full && !category.quotas?.full;

  const needsLicense = category.preServices.licenseType === PRE_SERVICE_LICENCE_TYPE.CAS
    && category.preServices.license
    && category.preServices.licenseMandatory;

  const currentUser = subscriptionFor ?? userDetail;

  const hasRightLicense = currentUser?.licenseCAS || !needsLicense;

  const hasCurrencyDivergence = cart?.currency && cart.currency !== event.currency;

  const showRegisterForm = isRegistrationOpen && logged && hasRightLicense && !hasCurrencyDivergence;

  return (
    <Container>
      <FormAndDescriptionContainer>
        {category.description && (
          <Description>
            {category.description}
          </Description>
        )}
        {showRegisterForm && event.hasDiscountVoucher && (
          <Voucher>
            <Icon name={ICON_NAME.info} color="var(--color-grey4)" />
            {t('eventCategoryListContent_discountVoucherText.message')}
          </Voucher>
        )}
        {!hasRightLicense && logged && (
          <CasBanner>
            <a href={config.licenseCAS?.shopUrl} target="_blank" rel="noreferrer">
              {t('eventCategoryListContent_youNeedCasLicense.message')}
            </a>
          </CasBanner>
        )}
        {hasCurrencyDivergence && logged && (
          <CurrencyBanner>
            {t('eventCategoryListContent_currencyDivergence.message', {
              cartCurrency: cart.currency,
              eventCurrency: event.currency,
            })}
          </CurrencyBanner>
        )}
        {showRegisterForm && preServices && (
          <FormContainer>
            <FormPreServices
              eventId={eventId}
              categoryId={categoryId}
              getFormikContext={context => {
                addFormikContext(categoryId, context);
              }}
              onSubmit={values => {
                dispatch(setSubscription({
                  category,
                  event,
                }));

                dispatch(setSubscriptionPreServices({
                  preServicesValues: omit(
                    values,
                    INSURANCE_ACCEPTANCE,
                    VIP_VALIDATION_INPUT.VIP_IS_VALID,
                    VIP_VALIDATION_INPUT.VIP_ERROR,
                  ),
                  categoryId,
                }));

                if (!pack) {
                  router.push(
                    getRoute.openModal(router)
                      .categoryServices(category._idMso, event._idMso),
                    null,
                    { scroll: false },
                  );
                }

                return { success: true };
              }}
            />
          </FormContainer>
        )}
      </FormAndDescriptionContainer>
      {showRegisterForm && !pack && (
        <SendButtonContainer>
          <Button
            onClick={() => {
              formikContexts[category._idMso]?.submitForm();
            }}
            leftIcon={ICON_NAME.shoppingCart}
            text={t('eventCategoryListContent_addToCart.message')}
            data-qa="eventDetail-addToCart-button"
          />
        </SendButtonContainer>
      )}
    </Container>
  );
};

export default EventCategoryListContent;

const Container = styled.div`
  display: grid;
  grid-template-columns: 5fr 1fr;
  grid-column-gap: 44px;
  grid-template-areas:
  "from-description send-button";

  @media (max-width: ${breakpoint.desktop - 300}px) {
    grid-template-columns: 1fr;
    grid-template-areas:
      "from-description"
      "send-button";
  }

  ${mediaBreakpoint.mobile} {
    grid-template-columns: 1fr;
    grid-template-areas:
      "from-description"
      "send-button";
  }
`;

const FormContainer = styled.div`
  margin-top: 20px;
`;

const FormAndDescriptionContainer = styled.div`
  grid-area: from-description;
`;

const Description = styled.div`
  ${textP};
  color: var(--color-grey4);
`;

const SendButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-end;

  grid-area: send-button;

  ${mediaBreakpoint.mobile} {
    button {
      width: 100%;
    }
  }
`;

const Voucher = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  margin: 20px 0;

  > :first-child {
    margin-right: 10px;
  }
`;

const CasBanner = styled(Banner)`
  margin-top: 10px;
  width: 500px;
  padding: 5px;
`;

const CurrencyBanner = styled(Banner)`
  margin-top: 10px;
  width: 100%;
`;
